import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../config/apiConfig';
import { getLocalizedAbilityDisplayName } from '../utils/highlightAbilities';
import {
  computeAttackStrategyStatistics,
  getAttackStrategyFactionRowColor,
  ATTACK_STRATEGY_PANEL_BOX_STYLE,
} from '../utils/attackStrategyStatistics';
import './AttackStrategyStatisticsPanel.css';

const MAX_BAR_HEIGHT = 60;
const RARITY_I18N_KEYS = ['common', 'rare', 'epic', 'legendary'];

function rarityLabel(rarity, t) {
  return t(`bank.${rarity}`, { defaultValue: rarity });
}

/**
 * War Effort attack-strategy statistics panels (by rarity, faction, cost, abilities).
 * @param {object[]} orders - One entry per strategy slot (duplicates included).
 * @param {'column'|'row'} layout - Panel stack direction.
 */
function AttackStrategyStatisticsPanel({ orders, layout = 'column' }) {
  const { t, i18n } = useTranslation();
  const [factionTranslations, setFactionTranslations] = useState({});

  useEffect(() => {
    let cancelled = false;

    const fetchFactionTranslations = async () => {
      try {
        const normalizedLanguage = (i18n.language || 'en-us').toLowerCase().replace('_', '-');
        const response = await fetch(
          `${API_URL}/factions?lang=${encodeURIComponent(normalizedLanguage)}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          }
        );
        if (!response.ok) {
          throw new Error(`factions HTTP ${response.status}`);
        }
        const factions = await response.json();
        if (cancelled) {
          return;
        }
        const translationMap = {};
        factions.forEach((faction) => {
          translationMap[faction.english_name] = faction.name;
        });
        setFactionTranslations(translationMap);
      } catch (err) {
        console.error('AttackStrategyStatisticsPanel: faction translation fetch failed:', err);
        if (!cancelled) {
          setFactionTranslations({});
        }
      }
    };

    fetchFactionTranslations();
    return () => {
      cancelled = true;
    };
  }, [i18n.language]);

  const stats = useMemo(() => computeAttackStrategyStatistics(orders), [orders]);
  const {
    costCounts,
    rarityCounts,
    factionEntries,
    abilityEntries,
    units,
    drones,
    weapons,
    stratagems,
    maxCostCount,
    maxRarityCount,
    RARITIES,
  } = stats;

  const getTranslatedFactionName = (factionName) => {
    if (!factionName) {
      return factionName;
    }
    return factionTranslations[factionName] || factionName;
  };

  const layoutClass =
    layout === 'row'
      ? 'attack-strategy-statistics-layout attack-strategy-statistics-layout--row'
      : 'attack-strategy-statistics-layout';

  return (
    <div className={layoutClass}>
      <div className="attack-strategy-right-panel" style={ATTACK_STRATEGY_PANEL_BOX_STYLE}>
        <div className="attack-strategy-rarity-graph-wrapper">
          <div className="attack-strategy-rarity-title">
            {t('strategy.statistics.byRarity', { defaultValue: 'By rarity' })}
          </div>
          <div className="attack-strategy-rarity-bar-track">
            <div className="attack-strategy-rarity-bar-container">
              {RARITIES.map((rarity) => {
                const count = rarityCounts[rarity] || 0;
                const barHeight = maxRarityCount > 0 ? (count / maxRarityCount) * MAX_BAR_HEIGHT : 0;
                return (
                  <div key={rarity} className={`attack-strategy-rarity-bar-segment rarity-${rarity}`}>
                    <div
                      className="attack-strategy-rarity-bar-fill"
                      style={{ height: `${barHeight}px`, minHeight: count > 0 ? '4px' : 0 }}
                      title={t('strategy.statistics.rarityTooltip', {
                        rarity: rarityLabel(rarity, t),
                        count,
                        defaultValue: `${rarityLabel(rarity, t)}: ${count} orders`,
                      })}
                    />
                  </div>
                );
              })}
            </div>
          </div>
          <div className="attack-strategy-rarity-labels-row">
            {RARITIES.map((r) => (
              <span key={r} className={`attack-strategy-rarity-bar-label rarity-${r}`}>
                {RARITY_I18N_KEYS.includes(r) ? rarityLabel(r, t) : r}
              </span>
            ))}
          </div>
          <div className="attack-strategy-rarity-counts-row">
            {RARITIES.map((r) => (
              <span key={r} className="attack-strategy-rarity-count">
                {rarityCounts[r] || 0}
              </span>
            ))}
          </div>
        </div>
        <div className="attack-strategy-faction-counts-wrapper">
          <div className="attack-strategy-rarity-title">
            {t('strategy.statistics.byFaction', { defaultValue: 'By faction' })}
          </div>
          <div className="attack-strategy-faction-counts-list">
            {factionEntries.map(([factionName, count]) => (
              <div
                key={factionName}
                className="attack-strategy-faction-count-row"
                style={{ borderLeftColor: getAttackStrategyFactionRowColor(factionName) }}
              >
                <span className="attack-strategy-faction-name">
                  {getTranslatedFactionName(factionName)}
                </span>
                <span className="attack-strategy-faction-count">{count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="attack-strategy-right-panel" style={ATTACK_STRATEGY_PANEL_BOX_STYLE}>
        <div className="attack-strategy-cost-graph-wrapper">
          <div className="attack-strategy-cost-title">
            {t('strategy.statistics.byCost', { defaultValue: 'By cost' })}
          </div>
          <div className="attack-strategy-cost-bar-container">
            {[...Array(13).keys()].map((cost) => {
              const count = costCounts[cost];
              const barHeight = maxCostCount > 0 ? (count / maxCostCount) * MAX_BAR_HEIGHT : 0;
              return (
                <div key={cost} className="attack-strategy-cost-bar-segment">
                  <div
                    className="attack-strategy-cost-bar-fill"
                    style={{ height: `${barHeight}px` }}
                    title={t('strategy.statistics.costTooltip', {
                      cost,
                      count,
                      defaultValue: `Cost ${cost}: ${count} orders`,
                    })}
                  />
                  <span className="attack-strategy-cost-bar-label">{cost}</span>
                </div>
              );
            })}
          </div>
        </div>
        <div className="attack-strategy-type-counters">
          <span>
            {t('strategy.statistics.units', { count: units, defaultValue: 'Units: {{count}}' })}
          </span>
          <span>
            {t('strategy.statistics.drones', { count: drones, defaultValue: 'Drones: {{count}}' })}
          </span>
          <span>
            {t('strategy.statistics.weapons', { count: weapons, defaultValue: 'Weapons: {{count}}' })}
          </span>
          <span>
            {t('strategy.statistics.stratagems', {
              count: stratagems,
              defaultValue: 'Stratagems: {{count}}',
            })}
          </span>
        </div>
      </div>
      <div className="attack-strategy-right-panel" style={ATTACK_STRATEGY_PANEL_BOX_STYLE}>
        <div className="attack-strategy-ability-counts-wrapper">
          <div className="attack-strategy-cost-title">
            {t('strategy.statistics.ordersWithAbilities', { defaultValue: 'Orders with abilities' })}
          </div>
          <div className="attack-strategy-ability-counts-list">
            {abilityEntries.length === 0 ? (
              <div className="attack-strategy-ability-count-row">
                <span className="attack-strategy-ability-name">—</span>
                <span className="attack-strategy-ability-count">
                  {t('strategy.statistics.none', { defaultValue: 'None' })}
                </span>
              </div>
            ) : (
              abilityEntries.map(([abilityName, count]) => (
                <div key={abilityName} className="attack-strategy-ability-count-row">
                  <span className="attack-strategy-ability-name">
                    {getLocalizedAbilityDisplayName(abilityName, i18n.language)}
                  </span>
                  <span className="attack-strategy-ability-count">{count}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AttackStrategyStatisticsPanel;
